'use strict';

import React from "react";
import { action, observable, configure, flow, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import {States,GENERIC_ERROR_MESSAGE} from "@uw-it-sis/lib-react/lib/AppConstants";
import events from "@uw-it-sis/lib-react/lib/Events";
import PlanEvents from "@uw-it-sis/lib-react-myplan/lib/PlanEvents";
import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import {prepareScheduleActivityOptions} from "./ScheduleBuilderUtils";
import {isEmpty} from "@uw-it-sis/lib-react/lib/Utils";

class ScheduleActivityDetailModalStore {
    state = States.initial;

    dirtyData = false;
    warnDuplicates = false;
    data = {};

    preparedScheduleActivity = {};

    constructor() {
        makeObservable(this, {
            state: observable,
            dirtyData: observable,
            warnDuplicates: observable,
            data: observable,
            preparedScheduleActivity: observable,
            mount: action,
            unmount: action,
            reset: action,
            resetData: action,
            toggleShowAlternates: action,
            _prepareSections: action
        });

        this.selectedActivityCodes = [];
        this.activityOption = {};
        this.hasAlternates = false;
        this.alternatePrimaryActivityIds = [];
        this.selectedAlternateCodes = [];
        this.analyticsStore = appStore.getAnalyticsStore();
        this.exceptionUtils = appStore.getExceptionUtils();
        this.httpClient = appStore.getHttpClient();
    }

    mount(props) {
        this.state = States.pending;

        events.listen(PlanEvents.planItemSelectionUpdated, (eo) => {
            this.state = States.pending;
            this.dirtyData = eo.dirty;
            this.selectedActivityCodes = eo.selectedActivityCodes;
            this.warnDuplicates = eo.duplicateSecondary;
            this.state = States.done;
        });

        this.data.showAlternates = false;

        this.activityOption = props.activity;

        if ( !props.registered ) {
            this._prepareSections();
        }

        this.state = States.done;
    }

    unmount() {
        events.remove(PlanEvents.planItemSelectionUpdated, (eo) => {
            this.dirtyData = eo.dirty;
            this.selectedActivityCodes = eo.selectedActivityCodes;
            this.warnDuplicates = eo.duplicateSecondary;
        });
    }
    reset() {
        this.state = States.pending;

        this.dirtyData = false;
        this.selectedActivityCodes = [];
        this.warnDuplicates = false;

        this.state = States.done;
    }

    resetData() {
        this.state = States.pending;

        this.data.submitted = false;
        this.data.errMsg = undefined;
        this.data.showAlternates = false;
        this.selectedAlternateCodes = [];

        this.state = States.done;
    }

    toggleShowAlternates() {
        this.state = States.pending;

        this.data.showAlternates = !this.data.showAlternates;

        this._prepareSections();

        this.state = States.done;
    }

    _prepareSections() {
        this.hasAlternates = false;
        this.alternatePrimaryActivityIds = [];

        let primaryCodeWithAlternates = undefined;
        // Check if the primary activity has alternates
        if (!isEmpty(this.activityOption.alternateActivities)) {
            primaryCodeWithAlternates = `${this.activityOption.courseCd} ${this.activityOption.activityCode}`;
        }
        // Check if the secondary activities have alternates
        this.activityOption.secondaryOptions.map( secondaryObj => {
            secondaryObj.activityOptions.map( secondaryActivity => {
                if (!isEmpty(secondaryActivity.alternateActivities)) {
                    this.selectedAlternateCodes.push(`${secondaryActivity.courseCd} ${secondaryActivity.activityCode}`);
                }
            });
        });

        if (this.selectedAlternateCodes.length > 0) {
            this.alternatePrimaryActivityIds.push(this.activityOption.parentActivityId);
            this.hasAlternates = true;
        }

        if (primaryCodeWithAlternates !== undefined) {
            this.alternatePrimaryActivityIds.push(this.activityOption.activityOfferingId);
            this.hasAlternates = true;
        }

        let showAlternatesCourseCodes = [];
        if (this.data.showAlternates) {
            showAlternatesCourseCodes.push(this.activityOption.activityOfferingId);
        }

        let result = prepareScheduleActivityOptions([this.activityOption], showAlternatesCourseCodes, this.alternatePrimaryActivityIds, this.selectedAlternateCodes);

        if (result && result.length > 0) {
            // since we are only passing one activity, there will be only one result
            this.preparedScheduleActivity = result[0];
        }
        else {
            this.preparedScheduleActivity = undefined;
        }
    }

    /**
     * Adds the selected sections of a course to plan
     *
     * @param termId the term to add the plan items
     * @param courseCd the course code for the activities being added
     * @param formCount the total number of form items ( both checked and unchecked ) ( for analytics )
     *
     */
    addSectionsToPlan = flow(function* (termId, courseCd, formCount) {
        this.state = States.pending;

        let postDataArr = [];
        this.selectedActivityCodes.map(code =>{
            let postData = {code: code};
            postDataArr.push(postData);
        });
        const eventObject = {
            code: "SB_ADD_REMOVE",
            data: this.selectedActivityCodes,
            description: 'Schedule Selection',
            isSection: false,
            atpId: termId,
            eventType: "planItemAdded"
        };

        const url = `api/plan/terms/${termId}/${encodeURIComponent(courseCd)}`;
        try {
            yield this.httpClient.doPatch(url, postDataArr,'add/remove sections to plan');
            if (appStore.isDevMode()) {
                console.log(`ScheduleActivityDetailModalStore: added section(s) to plan for ${termId}`);
            }

            // a successful action in this case will result in plan items being added
            events.emit(PlanEvents.planItemAdded, eventObject);
            let additionalData = {
                selected: this.selectedActivityCodes.length,
                deselected: formCount - this.selectedActivityCodes.length
            }
            this.analyticsStore.addEvent("SB-course-selection",{}, additionalData);

            this.data.submitted = true;
            this.state = States.done;
        } catch(error) {
            switch(error) {
                case 401:
                    this.exceptionUtils.handleException(error, "ScheduleActivityDetailModalStore.addSectionsToPlan");
                    this.state = States.error;
                    break;
                default:
                    this._handlePlanItemError(error);
                    break;
            }
        }
    }.bind(this));

    /**
     * Handle any errors during adding a section to plan
     */
    _handlePlanItemError(error) {
        // Can't use exception utils here because we need more granular handling. See if this error
        // is one of ours. If not, use the generic message.
        if (error.exceptionCode) {
            this.errorMessage = error.message;
        } else {
            this.errorMessage = GENERIC_ERROR_MESSAGE
        }

        this.data.errMsg = this.errorMessage;
        this.state = States.error;
    }

    getPlannedActivities(item, planData){
        let plannedCourseActivities = [];
        // Check if the primary has any alternates
        if (item.primary && planData.includes(item.activityOfferingId)){
            plannedCourseActivities.push(item);
            if (item.alternateActivities.length > 0){
                item.alternateActivities.map( altItem => {
                    if (planData.includes(altItem.activityOfferingId)) {
                        plannedCourseActivities.push(altItem);
                    }
                });
            }
        }

        // First level contains the primary section, secondary sections are within the list of secondaryOptions
        item.secondaryOptions.map(activityOptionsInfo => {
            activityOptionsInfo.activityOptions.map(activity => {
                if (planData.includes(activity.activityOfferingId)) {
                    plannedCourseActivities.push(activity);
                }
                activity.alternateActivities.map( altItem => {
                    if (planData.includes(altItem.activityOfferingId)) {
                        plannedCourseActivities.push(altItem);
                    }
                });
            })
        });

        if (item.hasOwnProperty("dupEnrolledActivities") && item.dupEnrolledActivities.length > 0) {
            item.dupEnrolledActivities.map(activity => {
                if (planData.includes(activity.activityOfferingId)) {
                    plannedCourseActivities.push(activity);
                }
            });
        }
        return plannedCourseActivities;
    }

}

export default ScheduleActivityDetailModalStore;